import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SeoHead from "@/components/SeoHead";
import Navbar from "@/components/landing/Navbar";
import FooterSection from "@/components/landing/FooterSection";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import NotFound from "./NotFound";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  og_image: string | null;
  heading: string;
  subheading: string | null;
  body_content: string | null;
  keywords: string[] | null;
  target_segment: string | null;
  cta_text: string | null;
  cta_url: string | null;
}

const SeoCampaignPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const fetchCampaign = async () => {
      const { data, error } = await supabase
        .from("seo_campaigns")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
      } else {
        setCampaign(data as Campaign);
        // Increment views
        supabase
          .from("seo_campaigns")
          .update({ views_count: (data.views_count || 0) + 1 })
          .eq("id", data.id)
          .then();
      }
      setLoading(false);
    };
    fetchCampaign();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !campaign) return <NotFound />;

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: campaign.meta_title || campaign.title,
    description: campaign.meta_description || campaign.subheading,
    url: `https://viamenu.com.br/s/${campaign.slug}`,
    publisher: {
      "@type": "Organization",
      name: "ViaMenu",
      url: "https://viamenu.com.br",
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={campaign.meta_title || campaign.title}
        description={campaign.meta_description || campaign.subheading || ""}
        ogTitle={campaign.meta_title || campaign.title}
        ogDescription={campaign.meta_description || campaign.subheading || ""}
        ogImage={campaign.og_image || undefined}
        canonicalUrl={`https://viamenu.com.br/s/${campaign.slug}`}
        keywords={campaign.keywords || undefined}
        schemaMarkup={schemaMarkup}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6"
          >
            {campaign.heading}
          </motion.h1>
          {campaign.subheading && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
            >
              {campaign.subheading}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button size="lg" asChild className="text-lg px-8 py-6">
              <Link to={campaign.cta_url || "/cadastro"}>
                {campaign.cta_text || "Comece Grátis"} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Body Content */}
      {campaign.body_content && (
        <section className="py-12 px-4">
          <div
            className="max-w-3xl mx-auto prose prose-lg dark:prose-invert prose-headings:font-display prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground"
            dangerouslySetInnerHTML={{ __html: campaign.body_content }}
          />
        </section>
      )}

      {/* Benefits */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-center mb-10 text-foreground">
            Por que escolher o ViaMenu?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "30 dias grátis para testar",
              "Sem taxa por pedido",
              "Delivery próprio sem comissão",
              "Cardápio digital com QR Code",
              "Painel da cozinha em tempo real",
              "Programa de fidelidade integrado",
              "Marketing com IA incluído",
              "Suporte por WhatsApp",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 text-center">
        <h2 className="text-3xl font-display font-bold text-foreground mb-4">
          Pronto para digitalizar seu negócio?
        </h2>
        <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
          Crie sua conta grátis e comece a receber pedidos em minutos.
        </p>
        <Button size="lg" asChild className="text-lg px-8 py-6">
          <Link to={campaign.cta_url || "/cadastro"}>
            {campaign.cta_text || "Comece Grátis"} <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </section>

      <FooterSection />
    </div>
  );
};

export default SeoCampaignPage;
